import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { temporal } from 'zundo'
import type { CanvasState } from './types'
import { useDesignStore } from './designStore'
import { useDebugStore } from './debugStore'
import { useTextDefaultsStore } from './textDefaultsStore'
import { createAddElementUseCase } from '../application/canvas/addElement'
import { createUpdateElementUseCase } from '../application/canvas/updateElement'
import { createRemoveElementUseCase } from '../application/canvas/removeElement'
import { createElementPositionService } from '../domain/canvas/services/elementPositionService'
import { transformOperations } from '../domain/canvas/value-objects/transform'
import { trackDesignEvent, trackElementInteraction } from '../infrastructure/analytics/gtm'

/**
 * Canvas Store
 * Manages canvas elements, selection, and undo/redo
 */

// Create service instances
const positionService = createElementPositionService()
// const validationService = createElementValidationService() // Will be used later

type CanvasActions = {
  // Element actions
  updateElementPosition: (elementId: string, position: { x: number; y: number }) => Promise<void>
  updateElementRotation: (elementId: string, rotation: number) => Promise<void>
  updateElementSize: (
    elementId: string,
    dimensions: { width: number; height: number },
  ) => Promise<void>
  updateTextFontSize: (elementId: string, fontSize: number) => Promise<void>
  updateTextContent: (elementId: string, content: string) => Promise<void>
  updateTextFont: (
    elementId: string,
    fontFamily: string,
    fontWeight: number,
    fontItalic: boolean,
  ) => Promise<void>
  updateTextColor: (elementId: string, color: string) => Promise<void>
  updateAssetColor: (elementId: string, color: string) => Promise<void>
  updateImageElement: (
    elementId: string,
    updates: { borderRadius?: number; filters?: any },
  ) => Promise<void>

  // Add/remove elements
  addTextElement: (centerInDesignZone?: boolean) => Promise<void>
  addAssetElement: (params: {
    src: string
    name: string
    assetId: string
    assetType: 'print' | 'engrave'
  }) => Promise<void>
  addImageElement: (params: {
    src: string
    width: number
    height: number
    centerInDesignZone?: boolean
  }) => Promise<void>
  deleteElement: (elementId: string) => Promise<void>

  // Selection
  selectElement: (elementId: string | null) => void
  deselectElement: () => void

  // Drag state
  startDrag: (elementId: string) => void
  endDrag: () => void

  // Canvas state
  setScale: (scale: number) => void
  toggleDebugPanel: () => void
  setModalOpen: (isOpen: boolean) => void

  // Elements management
  setElements: (elements: CanvasState['elements']) => void
  setLoadingState: (state: { fontsLoaded?: boolean; imagesLoaded?: boolean }) => void
  forceUpdate: () => void

  // Export
  startExport: () => void
  endExport: (success: boolean, filename?: string) => void

  // Centering actions
  centerElementHorizontally: (elementId: string) => Promise<void>
  centerElementVertically: (elementId: string) => Promise<void>
  centerElementInDesignZone: (elementId: string) => Promise<void>

  // Copy/Paste actions
  copyElement: (elementId: string) => void
  pasteElement: () => Promise<void>

  // Layer management
  sendToBack: (elementId: string) => void
  bringToFront: (elementId: string) => void

  // Flip actions
  flipElementHorizontally: (elementId: string) => Promise<void>

  // Reset
  reset: () => void
}

const initialState: CanvasState = {
  elements: {},
  selectedElementId: null,
  isDragging: false,
  draggedElementId: null,
  history: [],
  historyIndex: -1,
  scale: 1,
  debugPanelOpen: false,
  isModalOpen: false,
  colorHistory: [],
  clipboardElement: null,
}

// Auto-save debounce timer
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null
let previousElementsJson = ''
let isInitialized = false
let isFirstLoad = true

// Loading state tracking
const loadingState = {
  fontsLoaded: false,
  imagesLoaded: false,
  elementsSet: false,
}

const checkIfFullyLoaded = () => {
  return loadingState.fontsLoaded && loadingState.imagesLoaded && loadingState.elementsSet
}

// Position update debounce timer for logging
let positionLogTimer: ReturnType<typeof setTimeout> | null = null
// Rotation update debounce timer for logging
let rotationLogTimer: ReturnType<typeof setTimeout> | null = null
// Text content update debounce timer for logging
let textContentLogTimer: ReturnType<typeof setTimeout> | null = null
// Text color update debounce timer for logging
let textColorLogTimer: ReturnType<typeof setTimeout> | null = null
// Text font size update debounce timer for logging
let textFontSizeLogTimer: ReturnType<typeof setTimeout> | null = null

// Helper to constrain element position within design zone boundaries (DEPRECATED - use positionService)
// const constrainPosition = (
//   position: { x: number; y: number },
//   _element: any,
// ): { x: number; y: number } => {
//   const designStore = useDesignStore.getState()
//   const { currentDesign } = designStore
//
//   if (!currentDesign) return position
//
//   const designZone = currentDesign.zones.design
//
//   // Simple constraint: center position cannot go outside design zone boundaries
//   // This ensures elements never completely disappear from the viewport
//   const minX = designZone.left
//   const maxX = designZone.left + designZone.width
//   const minY = designZone.top
//   const maxY = designZone.top + designZone.height
//
//   // Constrain position to design zone boundaries
//   const constrainedX = Math.max(minX, Math.min(maxX, position.x))
//   const constrainedY = Math.max(minY, Math.min(maxY, position.y))
//
//   return { x: constrainedX, y: constrainedY }
// }

// Helper to add color to history
const addToColorHistory = (color: string, currentHistory: string[]): string[] => {
  // Don't add if it's already the most recent color
  if (currentHistory[0] === color) return currentHistory

  // Remove the color if it exists elsewhere in the history
  const filteredHistory = currentHistory.filter((c) => c !== color)

  // Add the new color at the beginning and keep only the last 5
  return [color, ...filteredHistory].slice(0, 5)
}

// Helper to log actions
const logAction = (
  actionName: string,
  payload: any,
  getState: () => CanvasState & CanvasActions,
) => {
  const debugStore = useDebugStore.getState()
  if (debugStore.isEnabled) {
    const state = getState()
    debugStore.logAction(actionName, payload, {
      elements: Object.keys(state.elements).length,
      selectedElementId: state.selectedElementId,
      scale: state.scale,
    })
  }
}

export const useCanvasStore = create<CanvasState & CanvasActions>()(
  devtools(
    subscribeWithSelector(
      temporal(
        (set, get) => ({
          ...initialState,

          updateElementPosition: async (elementId, position) => {
            const useCase = createUpdateElementUseCase({
              getElement: (id) => get().elements[id],
              constrainPosition: (pos, element) => {
                const design = useDesignStore.getState().currentDesign
                if (!design) return pos
                return positionService.constrainToDesignZone(pos, element, design.zones.design)
              },
              validatePosition: (pos, _element) => {
                const design = useDesignStore.getState().currentDesign
                if (!design) return true
                return positionService.isPositionValid(pos, design.zones.design)
              },
            })

            const result = await useCase.execute({
              elementId,
              updates: { position },
            })

            if (result.success && result.element) {
              // Check if we're dragging - if so, update without triggering temporal
              const { isDragging } = get()

              if (isDragging) {
                // During drag, update state without triggering temporal by using a direct state mutation
                // This is a special case where we want smooth UI updates without history tracking
                set(
                  (state) => ({
                    elements: {
                      ...state.elements,
                      [elementId]: result.element!,
                    },
                  }),
                  false,
                  { type: 'updateElementPosition/dragging', elementId },
                )
              } else {
                // Normal update that should be tracked in history
                set((state) => ({
                  elements: {
                    ...state.elements,
                    [elementId]: result.element!,
                  },
                }))
              }

              // Debounce the action logging to avoid spam
              if (positionLogTimer) {
                clearTimeout(positionLogTimer)
              }
              positionLogTimer = setTimeout(() => {
                logAction(
                  'updateElementPosition',
                  { elementId, position: result.element!.position },
                  get,
                )
                const element = get().elements[elementId]
                if (element) {
                  trackElementInteraction(
                    'update',
                    element.type as 'text' | 'image' | 'asset' | 'shape',
                    elementId,
                  )
                }
              }, 100) // Log after 100ms of no movement
            }
          },

          updateElementRotation: async (elementId, rotation) => {
            // Check if we're dragging - rotation can happen during gesture handling
            const { isDragging } = get()

            // Update immediately for smooth UI
            if (isDragging) {
              // During drag/gesture, update without triggering temporal
              set(
                (state) => ({
                  elements: {
                    ...state.elements,
                    [elementId]: {
                      ...state.elements[elementId],
                      transform: {
                        ...state.elements[elementId].transform,
                        rotation,
                      },
                    },
                  },
                }),
                false,
                { type: 'updateElementRotation/dragging', elementId },
              )
            } else {
              set((state) => ({
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...state.elements[elementId],
                    transform: {
                      ...state.elements[elementId].transform,
                      rotation,
                    },
                  },
                },
              }))
            }

            // Debounce the action logging to avoid spam
            if (rotationLogTimer) {
              clearTimeout(rotationLogTimer)
            }
            rotationLogTimer = setTimeout(() => {
              logAction('updateElementRotation', { elementId, rotation }, get)
              const element = get().elements[elementId]
              if (element) {
                trackElementInteraction(
                  'update',
                  element.type as 'text' | 'image' | 'asset' | 'shape',
                  elementId,
                )
              }
            }, 100) // Log after 100ms of no movement

            // TODO: Emit domain event if needed
          },

          updateElementSize: async (elementId, dimensions) => {
            const element = get().elements[elementId]
            if (!element || !element.dimensions) return

            // Calculate scale factors based on original dimensions
            const newScaleX = dimensions.width / element.dimensions.width
            const newScaleY = dimensions.height / element.dimensions.height

            // Preserve the sign (flip state) from existing transform
            const existingScaleX = element.transform?.scaleX || 1
            const existingScaleY = element.transform?.scaleY || 1
            const scaleX = existingScaleX < 0 ? -newScaleX : newScaleX
            const scaleY = existingScaleY < 0 ? -newScaleY : newScaleY

            logAction('updateElementSize', { elementId, dimensions, scaleX, scaleY }, get)
            trackElementInteraction(
              'update',
              element.type as 'text' | 'image' | 'asset' | 'shape',
              elementId,
            )

            set((state) => ({
              elements: {
                ...state.elements,
                [elementId]: {
                  ...state.elements[elementId],
                  transform: {
                    ...state.elements[elementId].transform,
                    scaleX,
                    scaleY,
                  },
                },
              },
            }))

            // TODO: Emit domain event if needed
          },

          updateTextFontSize: async (elementId, fontSize) => {
            // Update immediately for smooth UI
            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'text') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    fontSize,
                  },
                },
              }
            })

            // Save as new default
            useTextDefaultsStore.getState().setDefaults({ fontSize })

            // Debounce the action logging to avoid spam during slider usage
            if (textFontSizeLogTimer) {
              clearTimeout(textFontSizeLogTimer)
            }
            textFontSizeLogTimer = setTimeout(() => {
              logAction('updateTextFontSize', { elementId, fontSize }, get)
              trackElementInteraction('update', 'text', elementId)
            }, 100) // Log after 100ms of no changes
          },

          updateTextContent: async (elementId, content) => {
            // Update immediately for smooth UI
            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'text') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    content,
                  },
                },
              }
            })

            // Debounce the action logging to avoid spam while typing
            if (textContentLogTimer) {
              clearTimeout(textContentLogTimer)
            }
            textContentLogTimer = setTimeout(() => {
              logAction('updateTextContent', { elementId, content }, get)
              trackElementInteraction('update', 'text', elementId)
            }, 300) // Log after 300ms of no typing
          },

          updateTextFont: async (elementId, fontFamily, fontWeight, fontItalic) => {
            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'text') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    fontFamily,
                    fontWeight,
                    fontItalic,
                  },
                },
              }
            })

            // Save font family as new default
            useTextDefaultsStore.getState().setDefaults({ fontFamily })

            logAction('updateTextFont', { elementId, fontFamily, fontWeight, fontItalic }, get)
            trackElementInteraction('update', 'text', elementId)
          },

          updateTextColor: async (elementId, color) => {
            // Update immediately for smooth UI
            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'text') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    color,
                  },
                },
                colorHistory: addToColorHistory(color, state.colorHistory),
              }
            })

            // Save as new default color
            useTextDefaultsStore.getState().setDefaults({ fontColor: color })

            // Debounce the action logging to avoid spam
            if (textColorLogTimer) {
              clearTimeout(textColorLogTimer)
            }
            textColorLogTimer = setTimeout(() => {
              logAction('updateTextColor', { elementId, color }, get)
              trackElementInteraction('update', 'text', elementId)
            }, 50) // 50ms debounce like asset color
          },

          updateAssetColor: async (elementId, color) => {
            logAction('updateAssetColor', { elementId, color }, get)
            trackElementInteraction('update', 'asset', elementId)

            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'asset') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    color,
                  },
                },
                colorHistory: addToColorHistory(color, state.colorHistory),
              }
            })
          },

          updateImageElement: async (elementId, updates) => {
            logAction('updateImageElement', { elementId, updates }, get)
            trackElementInteraction('update', 'image', elementId)

            set((state) => {
              const element = state.elements[elementId]
              if (!element || element.type !== 'image') return state

              return {
                elements: {
                  ...state.elements,
                  [elementId]: {
                    ...element,
                    ...updates,
                  },
                },
              }
            })
          },

          addTextElement: async (centerInDesignZone = true) => {
            const design = useDesignStore.getState().currentDesign
            const textDefaults = useTextDefaultsStore.getState().defaults
            const useCase = createAddElementUseCase({
              getCurrentDesign: () => design,
              generateElementId: (type) =>
                `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              constrainPosition: (position, element) => {
                if (!design) return position
                return positionService.constrainToDesignZone(position, element, design.zones.design)
              },
            })

            // Use baseColor for engrave designs, otherwise use defaults
            const color =
              design?.type === 'engrave' && design.baseColor
                ? design.baseColor
                : textDefaults.fontColor

            const result = await useCase.execute({
              type: 'text',
              centerInDesignZone,
              color,
              fontFamily: textDefaults.fontFamily,
              fontSize: textDefaults.fontSize,
            })

            if (result.success && result.element) {
              logAction('addTextElement', { elementId: result.element.id, centerInDesignZone }, get)
              trackElementInteraction('add', 'text', result.element.id)

              set((state) => ({
                elements: {
                  ...state.elements,
                  [result.element!.id]: result.element!,
                },
                selectedElementId: result.element!.id,
              }))
            }
          },

          addAssetElement: async ({ src, name, assetId, assetType }) => {
            const design = useDesignStore.getState().currentDesign
            const useCase = createAddElementUseCase({
              getCurrentDesign: () => design,
              generateElementId: (type) =>
                `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              constrainPosition: (position, element) => {
                if (!design) return position
                return positionService.constrainToDesignZone(position, element, design.zones.design)
              },
            })

            // Use baseColor for engrave designs with engrave assets
            const color =
              design?.type === 'engrave' && design.baseColor && assetType === 'engrave'
                ? design.baseColor
                : undefined

            const result = await useCase.execute({
              type: 'asset',
              src,
              name,
              assetId,
              assetType,
              color,
              centerInDesignZone: true,
            })

            if (result.success && result.element) {
              logAction(
                'addAssetElement',
                { src, name, assetId, assetType, elementId: result.element.id },
                get,
              )
              trackElementInteraction('add', 'asset', result.element.id)

              set((state) => ({
                elements: {
                  ...state.elements,
                  [result.element!.id]: result.element!,
                },
                selectedElementId: result.element!.id,
              }))
            }
          },

          addImageElement: async ({ src, width, height, centerInDesignZone = true }) => {
            const useCase = createAddElementUseCase({
              getCurrentDesign: () => useDesignStore.getState().currentDesign,
              generateElementId: (type) =>
                `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              constrainPosition: (position, element) => {
                const design = useDesignStore.getState().currentDesign
                if (!design) return position
                return positionService.constrainToDesignZone(position, element, design.zones.design)
              },
            })

            const result = await useCase.execute({
              type: 'image',
              src,
              width,
              height,
              centerInDesignZone,
            })

            if (result.success && result.element) {
              // Calculate if image needs to be scaled to fit design zone
              const design = useDesignStore.getState().currentDesign
              let finalElement = result.element

              if (design && centerInDesignZone) {
                const designZone = design.zones.design
                const maxWidth = designZone.width * 0.8 // 80% of design zone width
                const maxHeight = designZone.height * 0.8 // 80% of design zone height

                // Check if image exceeds design zone dimensions
                if (width > maxWidth || height > maxHeight) {
                  // Calculate scale to fit
                  const scaleX = maxWidth / width
                  const scaleY = maxHeight / height
                  const scale = Math.min(scaleX, scaleY) // Use the smaller scale to ensure it fits

                  // Apply scale to the element
                  finalElement = {
                    ...result.element,
                    transform: {
                      ...result.element.transform,
                      scaleX: scale,
                      scaleY: scale,
                    },
                  }
                }
              }

              logAction(
                'addImageElement',
                {
                  elementId: finalElement.id,
                  src,
                  width,
                  height,
                  centerInDesignZone,
                  scaled: finalElement.transform?.scaleX !== 1,
                },
                get,
              )
              trackElementInteraction('add', 'image', finalElement.id)

              set((state) => ({
                elements: {
                  ...state.elements,
                  [finalElement.id]: finalElement,
                },
                selectedElementId: finalElement.id,
              }))
            }
          },

          deleteElement: async (elementId) => {
            const useCase = createRemoveElementUseCase({
              elementExists: (id) => !!get().elements[id],
              getSelectedElementId: () => get().selectedElementId,
            })

            const result = await useCase.execute({ elementId })

            if (result.success) {
              const element = get().elements[elementId]
              logAction('deleteElement', { elementId }, get)
              if (element) {
                trackElementInteraction(
                  'delete',
                  element.type as 'text' | 'image' | 'asset' | 'shape',
                  elementId,
                )
              }

              set((state) => {
                // Create a new elements object without the deleted element
                const newElements = { ...state.elements }
                delete newElements[elementId]

                return {
                  elements: newElements,
                  // Clear selection if the deleted element was selected
                  selectedElementId:
                    state.selectedElementId === elementId ? null : state.selectedElementId,
                }
              })
            }
          },

          selectElement: (elementId) => {
            logAction('selectElement', { elementId }, get)
            set({ selectedElementId: elementId })
            if (elementId) {
              const element = get().elements[elementId]
              if (element) {
                trackElementInteraction(
                  'select',
                  element.type as 'text' | 'image' | 'asset' | 'shape',
                  elementId,
                )
              }
            }
          },

          deselectElement: () => {
            const currentSelected = get().selectedElementId
            if (currentSelected) {
              logAction('deselectElement', { previousElementId: currentSelected }, get)
              const element = get().elements[currentSelected]
              if (element) {
                trackElementInteraction(
                  'deselect',
                  element.type as 'text' | 'image' | 'asset' | 'shape',
                  currentSelected,
                )
              }
              set({ selectedElementId: null })
            }
          },

          startDrag: (elementId) => {
            // Pause temporal tracking when drag starts
            const temporal = useCanvasStore.temporal.getState()
            temporal.pause()

            set({ isDragging: true, draggedElementId: elementId })
          },

          endDrag: () => {
            // Resume temporal tracking
            const temporal = useCanvasStore.temporal.getState()
            temporal.resume()

            // Clear drag state - this change will be tracked by temporal
            set({ isDragging: false, draggedElementId: null })
          },

          setScale: (scale) => {
            set({ scale })
          },

          toggleDebugPanel: () => {
            set((state) => ({ debugPanelOpen: !state.debugPanelOpen }))
          },

          setModalOpen: (isOpen) => {
            set({ isModalOpen: isOpen })
          },

          setElements: (elements) => {
            const elementCount = Object.keys(elements).length
            logAction('setElements', { elementCount }, get)

            // Apply baseColor to elements when design type is 'engrave'
            const design = useDesignStore.getState().currentDesign
            let processedElements = elements

            if (design?.type === 'engrave' && design.baseColor) {
              processedElements = Object.entries(elements).reduce(
                (acc, [id, element]) => {
                  // Apply baseColor to text elements
                  if (element.type === 'text') {
                    // eslint-disable-next-line no-param-reassign
                    acc[id] = { ...element, color: design.baseColor }
                  }
                  // Apply baseColor to engrave assets
                  else if (element.type === 'asset' && element.assetType === 'engrave') {
                    // eslint-disable-next-line no-param-reassign
                    acc[id] = { ...element, color: design.baseColor }
                  }
                  // Keep other elements unchanged
                  else {
                    // eslint-disable-next-line no-param-reassign
                    acc[id] = element
                  }
                  return acc
                },
                {} as typeof elements,
              )
            }

            // Extract unique colors from existing elements
            const existingColors = new Set<string>()
            Object.values(processedElements).forEach((element) => {
              if (element.type === 'text' && element.color) {
                existingColors.add(element.color)
              } else if (
                element.type === 'asset' &&
                element.assetType === 'engrave' &&
                element.color
              ) {
                existingColors.add(element.color)
              }
            })

            // Initialize color history with existing colors (up to 5)
            const colorHistoryFromElements = Array.from(existingColors).slice(0, 5)

            set({
              elements: processedElements,
              colorHistory: colorHistoryFromElements,
            })

            // Mark elements as set
            loadingState.elementsSet = true

            // Mark as initialized and store the initial state
            if (!isInitialized) {
              isInitialized = true
              previousElementsJson = JSON.stringify(processedElements)

              // Check if everything is loaded
              if (checkIfFullyLoaded()) {
                isFirstLoad = false
              }
            }
          },

          setLoadingState: (state) => {
            if (state.fontsLoaded !== undefined) {
              loadingState.fontsLoaded = state.fontsLoaded
            }
            if (state.imagesLoaded !== undefined) {
              loadingState.imagesLoaded = state.imagesLoaded
            }

            // Check if everything is loaded now
            if (isInitialized && checkIfFullyLoaded() && isFirstLoad) {
              isFirstLoad = false
            }
          },

          forceUpdate: () => {
            // Force a re-render by updating elements with the same values
            // This is useful when fonts are loaded and we need to re-render text
            set((state) => ({ elements: { ...state.elements } }))
          },

          startExport: () => {
            logAction('startExport', {}, get)
            trackDesignEvent('export_start', 'export', 'svg')
          },

          endExport: (success, filename) => {
            logAction('endExport', { success, filename }, get)
            trackDesignEvent(success ? 'export_success' : 'export_failed', 'export', filename)
          },

          centerElementHorizontally: async (elementId) => {
            const element = get().elements[elementId]
            const design = useDesignStore.getState().currentDesign
            if (!element || !design) return

            const exportZone = design.zones.export
            // centerPosition is already an offset within the export zone, not an absolute position
            // Element positions are relative to export zone (0,0)
            const centerX = exportZone.centerPosition?.x ?? exportZone.width / 2

            // Keep the same Y position
            await get().updateElementPosition(elementId, {
              x: centerX,
              y: element.position.y,
            })

            logAction('centerElementHorizontally', { elementId }, get)
          },

          centerElementVertically: async (elementId) => {
            const element = get().elements[elementId]
            const design = useDesignStore.getState().currentDesign
            if (!element || !design) return

            const exportZone = design.zones.export
            // centerPosition is already an offset within the export zone, not an absolute position
            // Element positions are relative to export zone (0,0)
            const centerY = exportZone.centerPosition?.y ?? exportZone.height / 2

            // Keep the same X position
            await get().updateElementPosition(elementId, {
              x: element.position.x,
              y: centerY,
            })

            logAction('centerElementVertically', { elementId }, get)
          },

          centerElementInDesignZone: async (elementId) => {
            const element = get().elements[elementId]
            const design = useDesignStore.getState().currentDesign
            if (!element || !design) return

            const exportZone = design.zones.export
            // centerPosition is already an offset within the export zone, not an absolute position
            // Element positions are relative to export zone (0,0)
            const centerX = exportZone.centerPosition?.x ?? exportZone.width / 2
            const centerY = exportZone.centerPosition?.y ?? exportZone.height / 2

            await get().updateElementPosition(elementId, {
              x: centerX,
              y: centerY,
            })

            logAction('centerElementInDesignZone', { elementId }, get)
          },

          copyElement: (elementId) => {
            const element = get().elements[elementId]
            if (!element) return

            // Deep clone the element to avoid reference issues
            set(() => ({
              clipboardElement: JSON.parse(JSON.stringify(element)),
            }))

            logAction('copyElement', { elementId }, get)
            trackElementInteraction(
              'update',
              element.type as 'text' | 'image' | 'asset' | 'shape',
              elementId,
            )
          },

          pasteElement: async () => {
            const { clipboardElement } = get()
            if (!clipboardElement) return

            // Generate new ID for pasted element
            const newId = `element-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

            // Add offset to position (10mm down and right)
            const offsetMm = 10
            const pastedElement = {
              ...clipboardElement,
              id: newId,
              position: {
                x: clipboardElement.position.x + offsetMm,
                y: clipboardElement.position.y + offsetMm,
              },
            }

            // Add to elements
            set((state) => ({
              elements: {
                ...state.elements,
                [newId]: pastedElement,
              },
              selectedElementId: newId, // Select the pasted element
            }))

            logAction('pasteElement', { newId, offset: offsetMm }, get)
            trackElementInteraction(
              'add',
              pastedElement.type as 'text' | 'image' | 'asset' | 'shape',
              newId,
            )
          },

          sendToBack: (elementId) => {
            const element = get().elements[elementId]
            if (!element) return

            // Find the minimum zIndex among all elements
            const allElements = Object.values(get().elements)
            const minZIndex = Math.min(...allElements.map((el) => el.zIndex || 0), 0)

            // Set the element's zIndex to be lower than the minimum
            set((state) => ({
              elements: {
                ...state.elements,
                [elementId]: {
                  ...state.elements[elementId],
                  zIndex: minZIndex - 1,
                },
              },
            }))

            logAction('sendToBack', { elementId }, get)
            trackElementInteraction(
              'update',
              element.type as 'text' | 'image' | 'asset' | 'shape',
              elementId,
            )
          },

          bringToFront: (elementId) => {
            const element = get().elements[elementId]
            if (!element) return

            // Find the maximum zIndex among all elements
            const allElements = Object.values(get().elements)
            const maxZIndex = Math.max(...allElements.map((el) => el.zIndex || 0), 0)

            // Set the element's zIndex to be higher than the maximum
            set((state) => ({
              elements: {
                ...state.elements,
                [elementId]: {
                  ...state.elements[elementId],
                  zIndex: maxZIndex + 1,
                },
              },
            }))

            logAction('bringToFront', { elementId }, get)
            trackElementInteraction(
              'update',
              element.type as 'text' | 'image' | 'asset' | 'shape',
              elementId,
            )
          },

          flipElementHorizontally: async (elementId) => {
            const element = get().elements[elementId]
            if (!element) return

            // Only allow flipping for assets
            if (element.type !== 'asset') return

            // Flip the element horizontally by inverting scaleX
            const newTransform = transformOperations.flipHorizontal(element.transform)

            set((state) => ({
              elements: {
                ...state.elements,
                [elementId]: {
                  ...state.elements[elementId],
                  transform: newTransform,
                },
              },
            }))

            logAction('flipElementHorizontally', { elementId }, get)
            trackElementInteraction('update', 'asset', elementId)
          },

          reset: () => {
            if (autoSaveTimer) {
              clearTimeout(autoSaveTimer)
              autoSaveTimer = null
            }
            if (positionLogTimer) {
              clearTimeout(positionLogTimer)
              positionLogTimer = null
            }
            if (rotationLogTimer) {
              clearTimeout(rotationLogTimer)
              rotationLogTimer = null
            }
            if (textContentLogTimer) {
              clearTimeout(textContentLogTimer)
              textContentLogTimer = null
            }
            if (textColorLogTimer) {
              clearTimeout(textColorLogTimer)
              textColorLogTimer = null
            }
            if (textFontSizeLogTimer) {
              clearTimeout(textFontSizeLogTimer)
              textFontSizeLogTimer = null
            }
            isInitialized = false
            isFirstLoad = true
            previousElementsJson = ''
            loadingState.fontsLoaded = false
            loadingState.imagesLoaded = false
            loadingState.elementsSet = false
            set(initialState)
          },
        }),
        {
          partialize: (state) => ({
            elements: state.elements,
            selectedElementId: state.selectedElementId,
            colorHistory: state.colorHistory,
            clipboardElement: state.clipboardElement,
          }),
          limit: 50,
        },
      ),
    ),
    {
      name: 'canvas-store',
    },
  ),
)

// Export temporal API for undo/redo
export const useCanvasTemporal = () => useCanvasStore.temporal

// Subscribe to element changes and trigger auto-save
useCanvasStore.subscribe(
  (state) => state.elements,
  (elements) => {
    // Skip if not initialized yet
    if (!isInitialized) {
      return
    }

    // Check if elements actually changed (not just re-rendered)
    const currentElementsJson = JSON.stringify(elements)
    if (currentElementsJson === previousElementsJson) {
      return
    }

    // Skip auto-save on first load (when elements are initially set)
    if (isFirstLoad) {
      isFirstLoad = false
      previousElementsJson = currentElementsJson
      return
    }
    previousElementsJson = currentElementsJson

    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer)
    }

    // Set new timer for auto-save (500ms debounce)
    autoSaveTimer = setTimeout(() => {
      const designStore = useDesignStore.getState()
      if (designStore.currentDesign && !designStore.isSaving) {
        // Convert elements to update format with all required fields
        const elementsArray = Object.values(elements).map((el) => {
          const baseElement = {
            elementId: el.id,
            type: el.type,
            centerPosition: {
              left: el.position.x,
              top: el.position.y,
            },
            rotation: el.transform?.rotation || 0,
            // Include transform string for scale/skew data (excluding rotation which is stored separately)
            transform: el.transform ? transformOperations.toCSSString(el.transform) : undefined,
          }

          if (el.type === 'text') {
            return {
              ...baseElement,
              value: el.content,
              fontSize: el.fontSize,
              fontFamily: el.fontFamily,
              fontColor: el.color,
              fontWeight: el.fontWeight,
              fontItalic: el.fontItalic,
            }
          } else if (el.type === 'asset') {
            return {
              ...baseElement,
              assetId: el.assetId,
              assetType: el.assetType,
              color: el.color,
            }
          } else if (el.type === 'image') {
            return {
              ...baseElement,
              value: el.src,
              borderRadius: el.borderRadius,
              filters: el.filters,
            }
          }

          return baseElement
        })

        designStore.saveDesign(elementsArray)
      }
    }, 500)
  },
)
